
import type { PropType } from 'vue';

import {
  defineComponent,
  reactive,
  ref,
  watch,
} from 'vue';

import type { CustomRevshare } from '@/types';

export default defineComponent({
  props: {
    modelValue: {
      type: Array as PropType<CustomRevshare[]>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    entryTitle: {
      type: String,
      required: true,
    },
    defaultRevshare: {
      type: Number,
      required: true,
    },
    keyValidator: {
      type: Function,
      default: (key: string | number | null) => !!key,
    },
    allowBulk: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const customRevshares = reactive<CustomRevshare[]>([...props.modelValue]);

    const addCustomRevshare = (): void => {
      customRevshares.push({
        key: null,
        revshare: props.defaultRevshare,
      });
    };

    const deleteCustomRevshare = (index: number): void => {
      customRevshares.splice(index, 1);
    };

    const bulkDialog = ref<boolean>(false);

    const bulkKeysInput = ref<string>('');

    const bulkRevshare = ref<number>(props.defaultRevshare);

    const bulkAddCustomRevshares = (): void => {
      const existingKeys = new Set(customRevshares.map((customRevshare: CustomRevshare) => customRevshare.key));

      bulkKeysInput
        .value
        .split('\n')
        .map((line: string) => line.trim())
        .filter((key: string) => props.keyValidator(key) && !existingKeys.has(key))
        .forEach((key: string) => {
          existingKeys.add(key);

          customRevshares.push({ key, revshare: bulkRevshare.value });
        });

      bulkDialog.value = false;

      bulkKeysInput.value = '';

      bulkRevshare.value = props.defaultRevshare;
    };

    watch(
      customRevshares,
      () => emit('update:modelValue', customRevshares.filter((customRevshare: CustomRevshare) => props.keyValidator(customRevshare.key))),
    );

    watch(
      () => props.defaultRevshare,
      (newValue: number) => {
        bulkRevshare.value = newValue;
      },
    );

    return {
      customRevshares,
      addCustomRevshare,
      deleteCustomRevshare,
      bulkDialog,
      bulkKeysInput,
      bulkRevshare,
      bulkAddCustomRevshares,
    };
  },
});
